import { z } from 'zod';

import {
  attributionModelSchema,
  attributionWindowSchema,
  type AttributionModel,
  type AttributionWindow,
} from './analytics.js';
import { listQuerySchema, sortDirSchema, type Capability, type ResponseMeta } from './common.js';
import {
  automationModeSchema,
  localeSchema,
  workspaceRoleSchema,
  type AutomationMode,
  type ConnectionStatus,
  type Locale,
  type SubscriptionStatus,
  type WorkspaceRole,
} from './enums.js';
import type { PlanTier } from './plans.js';

/**
 * WORKSPACES — kiracı çekirdeği, ekip, profil, güvenlik, GDPR.
 *
 * Tasarım §6 (multi-tenancy), §10 (güvenlik ve gizlilik).
 *
 * ---------------------------------------------------------------------------
 * 🔴 Bu alan diğer yedisinin ön koşulu
 * ---------------------------------------------------------------------------
 * `WorkspaceContextGuard`, `@CurrentWorkspace()`, `@RequireCapability()` ve
 * Prisma kiracı katmanı burada doğuyor. Bunlar olmadan hiçbir kiracı ucu
 * güvenli değil — §6 bunu "unutulabilir konvansiyon değil OTOMATİK KURAL"
 * diye yazmış.
 *
 * Prisma katmanının davranışı: kiracıya ait 17 modelde `where.workspaceId`
 * yoksa sorgu FIRLATIR. Sessizce eklemez. Sessiz ekleme, yanlış yazılmış bir
 * sorguyu gizler ve bir gün doğru görünüp yanlış veri döndürür; fırlatma
 * testte hemen yakalatır.
 *
 * `/admin` yolu bu kuraldan AÇIKÇA muaf ve muafiyet kodda yorumlanmış olmalı —
 * kazara değil, yazarak. Muafiyetin testi de yazılır: `/admin/overview`'daki
 * sayımlar `workspaceId` filtresi taşımıyor ve taşımamalı.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Kabuk bağlamı — her sayfanın ilk çağrısı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * KB3–KB7 beş ayrı istek atmasın diye tek uç.
 *
 * Kabuk her sayfa geçişinde bunu çağırıyor; hafif tutuldu (sayımlar ve
 * rozetler, liste yok). Ağır olan her şey kendi ucunda.
 */
export interface WorkspaceContextResponse {
  workspace: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    currency: string;
    createdAt: string;
  };
  /// İstek sahibinin rolü ve o rolün yapabildikleri — arayüz düğmeyi
  /// gizlerken kendi tablosunu yazmasın.
  role: WorkspaceRole;
  capabilities: Capability[];
  /// KB7 plan rozeti. `null` = bu çalışma alanı için hiç plan seçilmemiş
  /// (yeni alanlar plansız açılıyor; gerekçe `workspaces.service.ts`te).
  plan: {
    tier: PlanTier;
    status: SubscriptionStatus;
    currentPeriodEnd: string;
    /// Deneme/dönem bitimine kalan tam gün. Negatif olabilir (süresi geçmiş).
    daysRemaining: number;
    graceUntil: string | null;
  } | null;
  /**
   * Kabuğun "plan seçin" şeridini basması gerekiyor mu.
   *
   * TÜRETİLMİŞ ama sunucuda türetiliyor: iki farklı gerçek aynı sonuca
   * çıkıyor (satır hiç yok / satır var ama `EXPIRED`–`CANCELED`) ve bunu
   * arayüzde her tüketiciye ayrı ayrı yazdırmak, `EntitlementService`in
   * varlık sebebiyle aynı hatayı üretirdi: biri `plan === null`a bakar ve
   * bitmiş aboneliği kaçırır, diğeri duruma bakar ve plansız alanı.
   *
   * ⚠️ Bu bayrak bir YETKİ KARARI DEĞİL — yalnız şeridin görünürlüğü.
   * "Şunu yapabilir miyim" sorusunun cevabı hep `GET /billing/entitlements`.
   */
  needsPlan: boolean;
  /// KB5 kredi rozeti — rezerve AYRI gösterilir, "kullanılabilir" ile
  /// karıştırılırsa kullanıcı olmayan krediye güvenir.
  credits: {
    planCredits: number;
    topupCredits: number;
    reservedCredits: number;
    available: number;
  } | null;
  /// KB6 zil.
  unreadNotifications: number;
  /// GB8 uyarı şeridi ve BG3.
  connectionHealth: {
    healthy: number;
    degraded: number;
    disconnected: number;
    total: number;
  };
  /// OB5 — yarım kalan onboarding genel bakışta hatırlatılır.
  onboarding: {
    hasConnection: boolean;
    hasSyncedData: boolean;
    hasWorkflow: boolean;
    complete: boolean;
  };
  /// KB4 tazelik etiketi.
  meta: ResponseMeta;
}

/**
 * KB3 switcher — kullanıcının üye olduğu workspace'ler.
 *
 * ---------------------------------------------------------------------------
 * Neden `plan` tek başına yetmiyor
 * ---------------------------------------------------------------------------
 * Seçicideki rozet üç ayrı gerçeği anlatmak zorunda: "Growth" (planlı),
 * "Deneme · 3 gün" (denemede) ve "Plan yok" (hiç abonelik satırı olmayan yeni
 * alan). Tier tek başına ilk ikisini ayıramıyor — `TRIAL` hem süren hem
 * bitmiş denemeyi anlatıyor — üçüncüsünü ise hiç söyleyemiyor.
 *
 * `status: null` = abonelik satırı YOK. `plan` da o durumda `null` kalır;
 * ikisi birlikte "bu alan için henüz plan seçilmedi" demek oluyor. Bitmiş
 * deneme ise `plan: TRIAL` + `status: EXPIRED` — farklı bir cümle, farklı
 * bir çağrı ("yenile" değil "seç").
 */
export interface WorkspaceSwitcherRow {
  id: string;
  name: string;
  slug: string;
  /// Kabuğun her yerinde gösterilen logo; seçicide de baş harflerin yerini alır.
  logoUrl: string | null;
  role: WorkspaceRole;
  plan: PlanTier | null;
  /// `null` = abonelik satırı yok (plan hiç seçilmemiş).
  status: SubscriptionStatus | null;
  /// Deneme/dönem bitişine kalan gün. Abonelik yoksa `null`.
  daysRemaining: number | null;
  memberCount: number;
  active: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kurulum akışı (OB) — GET /workspaces/current/onboarding
// ═══════════════════════════════════════════════════════════════════════════

/**
 * OB1–OB5 — "kuruluma devam et" ekranının TÜRETİLMİŞ durumu.
 *
 * ---------------------------------------------------------------------------
 * Neden adım tablosu ya da adım kolonu YOK
 * ---------------------------------------------------------------------------
 * Altı adımın da cevabı zaten var olan satırlarda yazılı: sektör
 * `workspaces.industry`de, bağlantı `ad_connections`ta, veri
 * `metrics_daily`de, kural `workflows`ta, davet `invitations`ta, plan
 * `subscriptions`ta. İkinci bir `onboarding_steps` tablosu aynı gerçeği iki
 * yerde saklamak olurdu — ve o iki yer AYRIŞIR: kullanıcı bağlantısını
 * silince adım tablosu "tamamlandı" demeye devam eder, kullanıcı da hiç
 * dönmeyecek bir yeşil tik görür. Türetme her istekte yeniden bakıyor ve
 * GERİYE de işliyor.
 *
 * TEK istisna `workspaces.onboardingSkippedAt`: "atladım" bir NİYET ve hiçbir
 * satırdan okunamıyor (şemadaki kolon yorumunun kendi gerekçesi).
 *
 * ---------------------------------------------------------------------------
 * Kabuktaki dört bayrak ile ilişkisi
 * ---------------------------------------------------------------------------
 * `WorkspaceContextResponse.onboarding` her sayfa geçişinde dönüyor ve HAFİF
 * kalmalı — orada dört bayrak var, burada altı adımın tamamı, rota ipuçları
 * ve yüzde. İkisi aynı gerçeği anlatıyor, bu yüzden `complete` alanı iki
 * yerde de AYNI formülü kullanmak zorunda (aşağıdaki 🔴 nota bak).
 */

export const ONBOARDING_STEP_KEYS = [
  'profile',
  'connection',
  'data',
  'automation',
  'team',
  'plan',
] as const;
export type OnboardingStepKey = (typeof ONBOARDING_STEP_KEYS)[number];

/**
 * Adımın DEĞİŞMEYEN yüzü — rota, uç ipucu, zorunluluk.
 *
 * Sunucudan dönmesine rağmen sabit: arayüz listeyi istek gelmeden de
 * basabilsin (iskelet) ve "hangi düğme nereye gider" iki yerde yazılmasın.
 * `done` tek başına sunucudan geliyor.
 */
export interface OnboardingStepDefinition {
  key: OnboardingStepKey;
  /**
   * Bu adım olmadan ürün ÇALIŞMIYOR mu.
   *
   * Yalnız `connection` ve `data` zorunlu: bağlı hesabı ve verisi olmayan bir
   * workspace'te hiçbir ekran anlamlı sayı gösteremiyor. Kalan dördü ürünü
   * daha iyi yapıyor ama kullanılabilirliğin koşulu değil — hepsini zorunlu
   * saymak, plan satın almamış kullanıcıya "kurulumun yarım" demek olurdu.
   */
  required: boolean;
  /// Arayüz rotası — adımın düğmesi buraya götürür.
  route: string;
  /**
   * Adımı tamamlayan API çağrıları. Çoğul, çünkü iki adımın İKİ ayrı yolu var:
   * bağlantı gerçek OAuth ile de demo veriyle de kurulabiliyor, kural
   * sıfırdan da şablondan da.
   *
   * `{platform}` / `{id}` yer tutucu — istemci kendi değerini koyuyor.
   */
  endpoints: readonly string[];
}

export const ONBOARDING_STEPS: readonly OnboardingStepDefinition[] = [
  {
    key: 'profile',
    required: false,
    route: '/dashboard/settings',
    endpoints: ['PATCH /workspaces/current/settings'],
  },
  {
    key: 'connection',
    required: true,
    route: '/dashboard/connections',
    endpoints: ['POST /integrations/{platform}/oauth/start', 'POST /demo'],
  },
  {
    // Kullanıcının "yapacağı" bir şey yok — ilk senkron bitince kendiliğinden
    // tamamlanıyor. Uç yine de veriliyor: bekleyen kullanıcı senkronu elle
    // tetikleyebilsin.
    key: 'data',
    required: true,
    route: '/dashboard/connections',
    endpoints: ['POST /integrations/accounts/{id}/sync'],
  },
  {
    key: 'automation',
    required: false,
    route: '/dashboard/automations/templates',
    endpoints: ['POST /automations', 'POST /automations/templates/install'],
  },
  {
    key: 'team',
    required: false,
    route: '/dashboard/settings/team',
    endpoints: ['POST /workspaces/current/invitations'],
  },
  {
    key: 'plan',
    required: false,
    route: '/dashboard/billing',
    endpoints: ['POST /billing/checkout'],
  },
];

export interface OnboardingStep extends OnboardingStepDefinition {
  done: boolean;
}

/**
 * İkinci adımın hangi yolla karşılandığı.
 *
 * `demo` ile `real` ayrımı arayüz için kritik: demo veriyle gezen kullanıcı
 * ekranda "bu sayılar örnek" şeridini görmeli, yoksa panelde gördüğü ROAS'ı
 * kendi hesabının ROAS'ı sanır. İkisi aynı anda doğru olabiliyor (önce demo
 * yazılıp sonra Meta bağlanırsa) ve o durumda `real` kazanıyor — gerçek veri
 * geldiği an ekranın sözü "bu sayılar örnek" olamaz.
 */
export type OnboardingDataSource = 'none' | 'demo' | 'real';

export interface OnboardingStatusResponse {
  /// `ONBOARDING_STEPS` sırasıyla, her birine `done` eklenmiş hâli.
  steps: OnboardingStep[];
  /**
   * Birinci adımın ham değerleri.
   *
   * Arayüz sektör alanını ikinci bir istek atmadan dolduruyor:
   * `GET /workspaces/current/settings` yanıtı (`WorkspaceSettings`) artık
   * `industry` taşıyor (Faz 8 kapanışında eklendi).
   */
  profile: { name: string; industry: string | null };
  dataSource: OnboardingDataSource;
  completedCount: number;
  totalCount: number;
  /// Kalan adım sayısı — zorunlu/isteğe bağlı ayırmadan.
  remainingCount: number;
  /// Yalnız ZORUNLU adımlardan kalan. `complete` bunun sıfır olması demek.
  remainingRequiredCount: number;
  /// 0–100, TÜM adımlar üzerinden. Halka göstergesinin okuduğu sayı.
  percent: number;
  /**
   * 🔴 `WorkspaceContextResponse.onboarding.complete` ile AYNI formül:
   * bağlantı var VE veri geldi. Ayrışırsa kabuktaki "kurulumu tamamla"
   * şeridi ile kurulum ekranı aynı an için farklı şey söyler — kullanıcı
   * şeridi kapatır, ekran hâlâ yarım der.
   *
   * Dikkat: `percent: 100` DEMEK DEĞİL. Zorunlu iki adım bitmiş olabilir ve
   * plan hâlâ deneme olabilir; ürün çalışır durumda ama liste yarımdır.
   */
  complete: boolean;
  /// `POST .../onboarding/skip` ile yazıldı. Null = atlanmadı.
  skippedAt: string | null;
  /**
   * Kurulum kartı gösterilsin mi — atlanmadıysa ve yapacak bir şey kaldıysa.
   *
   * Üç alanı (skippedAt, completedCount, totalCount) arayüzde birleştirmek
   * yerine sunucuda türetiliyor: aynı `&&` zinciri kabukta, genel bakışta ve
   * kurulum sayfasında üç kez yazılırdı ve biri güncellenmeyi kaçırırdı.
   */
  visible: boolean;
}

/** Atlama/geri alma yanıtı — tek alan, çünkü tek kolon değişiyor. */
export interface OnboardingSkipResponse {
  skippedAt: string | null;
}

// ─────────────────────── Eğitim videoları (statik katalog) ───────────────────────

/**
 * 🔴 SAĞLAYICI KİMLİKLERİ HENÜZ YOK — `providerId` bilerek `null`.
 *
 * Gerçek kimlikler ÇEKİM SONRASI doldurulacak. Buraya uydurma bir YouTube
 * kimliği yazmak, arayüzde "oynat" düğmesi gösterip kullanıcıyı silinmiş bir
 * videoya ya da bambaşka birinin içeriğine götürürdü — ve hata, videolar
 * çekilene kadar kimsenin fark etmeyeceği bir yerde beklerdi.
 *
 * `onboardingVideosReady()` bütün katalog için tek kontrol veriyor: arayüz
 * "videolarla öğren" bölümünü o `false` iken hiç basmıyor.
 */
export interface OnboardingVideo {
  /// Hangi adımı anlatıyor — `ONBOARDING_STEPS` ile aynı anahtar.
  step: OnboardingStepKey;
  title: Record<Locale, string>;
  /**
   * PLANLANAN uzunluk, saniye. Senaryo hedefi; çekim sonrası gerçek süreyle
   * değiştirilecek.
   *
   * ⚠️ `providerId === null` iken arayüzde GÖSTERİLMEZ: video yokken "2:30"
   * yazmak, olmayan bir şeye ölçü vermek olur.
   */
  durationSeconds: number;
  /// Şimdilik tek sağlayıcı. Alan yine de var: barındırma değişirse
  /// (Vimeo, kendi CDN'imiz) her giriş kendi kaynağını söylesin.
  provider: 'youtube';
  providerId: string | null;
}

export const ONBOARDING_VIDEOS: readonly OnboardingVideo[] = [
  {
    step: 'profile',
    title: {
      tr: 'Çalışma alanınızı tanıtın',
      en: 'Introduce your workspace',
    },
    durationSeconds: 75,
    provider: 'youtube',
    providerId: null,
  },
  {
    step: 'connection',
    title: {
      tr: 'Reklam hesabınızı bağlayın (ya da demo veriyle gezin)',
      en: 'Connect your ad account (or explore with demo data)',
    },
    durationSeconds: 180,
    provider: 'youtube',
    providerId: null,
  },
  {
    step: 'data',
    title: {
      tr: 'İlk senkron: veriler ne zaman gelir',
      en: 'First sync: when your data arrives',
    },
    durationSeconds: 120,
    provider: 'youtube',
    providerId: null,
  },
  {
    step: 'automation',
    title: {
      tr: 'İlk otomasyon kuralınızı kurun',
      en: 'Set up your first automation rule',
    },
    durationSeconds: 240,
    provider: 'youtube',
    providerId: null,
  },
  {
    step: 'team',
    title: {
      tr: 'Ekibinizi davet edin ve rolleri seçin',
      en: 'Invite your team and choose roles',
    },
    durationSeconds: 105,
    provider: 'youtube',
    providerId: null,
  },
  {
    step: 'plan',
    title: {
      tr: 'Planınızı seçin: harcama, kredi ve koltuk',
      en: 'Pick your plan: spend, credits and seats',
    },
    durationSeconds: 150,
    provider: 'youtube',
    providerId: null,
  },
];

/** Adımın videosu. Katalog eksik kalırsa `undefined` — çağıran dallanır. */
export function onboardingVideoFor(step: OnboardingStepKey): OnboardingVideo | undefined {
  return ONBOARDING_VIDEOS.find((video) => video.step === step);
}

/**
 * Katalogda oynatılabilir tek bir video var mı.
 *
 * Arayüz bölümü buna bakarak hiç basmıyor. Tek tek `providerId` kontrolü de
 * gerekiyor (bazıları çekilip bazıları çekilmemiş olabilir) ama "bölüm
 * görünsün mü" sorusunun cevabı bu.
 */
export function onboardingVideosReady(): boolean {
  return ONBOARDING_VIDEOS.some((video) => video.providerId !== null);
}

// ═══════════════════════════════════════════════════════════════════════════
// Workspace CRUD ve ayarlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 Kullanıcı başına workspace sayısı SINIRLI.
 *
 * Kayıt akışı her workspace için TRIAL abonelik + 25 kredi + defter satırı
 * açıyor. Sınırsız workspace = sınırsız bedava kredi; bir kullanıcı döngüyle
 * istediği kadar üretebilir.
 *
 * Karar: `POST /workspaces` ile açılan workspace TRIAL aboneliği alır ama
 * KREDİ ALMAZ (`MONTHLY_GRANT` yazılmaz, bakiye 0 açılır). Ücretsiz 25 kredi
 * yalnızca KAYIT anındaki ilk workspace'e verilir. Ayrıca kullanıcı başına
 * sahip olunabilecek workspace sayısı sınırlı.
 *
 * Ajans senaryosu (§6) bozulmuyor: ajans zaten plan satın alıyor ve kredi
 * plandan geliyor.
 */
export const MAX_OWNED_WORKSPACES_PER_USER = 5;
export const NEW_WORKSPACE_GETS_TRIAL_CREDITS = false;

export const workspaceCreateSchema = z.object({
  name: z.string().trim().min(2, 'Çalışma alanı adı en az 2 karakter').max(80),
  /// IANA zaman dilimi. Boşsa kullanıcının mevcut workspace'inden kopyalanır.
  timezone: z.string().max(64).optional(),
  /// ISO 4217, üç büyük harf.
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Para birimi ISO 4217 olmalı (USD, TRY, EUR…)')
    .optional(),
});
export type WorkspaceCreateInput = z.infer<typeof workspaceCreateSchema>;

/**
 * AY1'deki "logo" alanının şemada KARŞILIĞI YOK. Bu turda baş harflerle
 * yetiniliyor (mevcut demo da öyle yapıyor); şema önerisi bildirildi.
 */
export const workspaceUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(80).optional(),
    timezone: z.string().max(64).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/).optional(),
    /**
     * Sektör — kurulum akışının BİRİNCİ adımının tek gerçek sinyali.
     *
     * Şemada (`workspaces.industry`) kolon zaten vardı ama yazılacak bir uç
     * yoktu; adım hiçbir zaman tamamlanamıyordu. Serbest metin, enum değil:
     * tek tüketicisi AI sistem prompt'u ve orada kategori değil bağlam
     * cümlesi gerekiyor (şemadaki kolon yorumu).
     *
     * `null` = temizle. `undefined` = dokunma. İkisini ayırmak şart, yoksa
     * "sektörü sildim" ile "sektöre dokunmadım" aynı isteğe düşerdi.
     */
    industry: z.string().trim().max(120).nullable().optional(),
    automationMode: automationModeSchema.optional(),
    /// AY4 — global guardrail'ler. Tavan burada; kural bunu AŞAMAZ (§4.2).
    maxBudgetChangePercent: z.number().int().min(1).max(100).optional(),
    maxActionsPerDay: z.number().int().min(1).max(1000).optional(),
    /**
     * Atıf varsayılanı — Faz 6. `resolveAttribution`ın ikinci basamağı.
     *
     * `null` = ürün varsayılanına dön, `undefined` = dokunma (industry ile
     * aynı ayrım). Bu ayar TÜM ekranları ve otomasyon motorunu birden
     * etkiliyor: kullanıcı "bizde dönüşüm 28 günlük tıklamadır" dediğinde
     * kuralın karar verdiği sayı da o pencereyle sayılıyor.
     *
     * ⚠️ `attributionModel` kaydedilir ama BUGÜN hiçbir sayıyı değiştirmez
     * (`AttributionInfo.modelApplied` her zaman `false`) — gerekçe
     * `analytics.ts` atıf bölümünde. Arayüz modeli, uygulanmadığını
     * söylemeden sunmamalı.
     */
    attributionWindow: attributionWindowSchema.nullable().optional(),
    attributionModel: attributionModelSchema.nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Değiştirilecek bir şey verin');
export type WorkspaceUpdateInput = z.infer<typeof workspaceUpdateSchema>;

export interface WorkspaceSettings {
  id: string;
  name: string;
  slug: string;
  /**
   * İşletmenin sektörü — `workspaceUpdateSchema.industry` ile yazılıyor.
   *
   * 🔴 Faz 8'de eklendi. Alan bir dönem YAZILABİLİYOR ama OKUNAMIYORDU:
   * PATCH kabul ediyordu, GET yanıtı taşımıyordu. Ayarlar formu kaydettiği
   * değeri geri okuyamayınca her açılışta boş görünüyor ve kullanıcı ya
   * kaydın gitmediğini sanıyor ya da üzerine boş yazıyordu.
   */
  industry: string | null;
  timezone: string;
  currency: string;
  automationMode: AutomationMode;
  maxBudgetChangePercent: number;
  maxActionsPerDay: number;
  /// Faz 6 — `null` ise ürün varsayılanı (`DEFAULT_ATTRIBUTION`) geçerli.
  /// Sunucu varsayılanı buraya KOPYALAMIYOR: kopyalasaydı arayüz "seçilmiş"
  /// ile "varsayılan" arasındaki farkı kaybederdi.
  attributionWindow: AttributionWindow | null;
  attributionModel: AttributionModel | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * AY6 — workspace silme. Yalnız OWNER, ÇİFT ONAY.
 *
 * Sıra önemli ve kuyruk işiyle tamamlanır:
 *   1. Platform jetonlarını iptal et (mümkünse) ve kayıtları sil
 *   2. Tekrarlayan işleri (senkron, kural, zamanlanmış rapor) kaldır
 *   3. S3 varlıklarını temizle (kuyruk işi — cascade S3'ü bilmiyor)
 *   4. Cascade delete
 *   5. Denetim satırı — silinen workspace'in DIŞINDA bir yere yazılır,
 *      çünkü kendi AuditLog satırları cascade ile gidiyor
 */
export const workspaceDeleteSchema = z.object({
  /// Workspace adının BİREBİR yazılması. Onay kutusu yeterli değil: bu işlem
  /// aylık geçmiş metriği de siliyor ve geri dönüşü yok.
  confirmName: z.string().min(1).max(80),
  reason: z.string().trim().max(500).optional(),
});
export type WorkspaceDeleteInput = z.infer<typeof workspaceDeleteSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Ekip
// ═══════════════════════════════════════════════════════════════════════════

export interface MemberRow {
  membershipId: string;
  userId: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: WorkspaceRole;
  joinedAt: string;
  /// İstek sahibinin kendisi mi — "kendini çıkaramazsın" düğmesi için.
  isSelf: boolean;
}

export interface InvitationRow {
  id: string;
  email: string;
  role: WorkspaceRole;
  invitedById: string;
  invitedByName: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  /// `acceptedAt`/`expiresAt`'ten TÜRETİLİR — şemada `status` kolonu yok.
  status: 'pending' | 'accepted' | 'expired';
}

export interface TeamResponse {
  members: MemberRow[];
  invitations: InvitationRow[];
  /**
   * EK8 — "5 üyeden 4'ü dolu". BEKLEYEN DAVETLER DE SAYILIR; yoksa limit
   * davetle aşılır: 5 kişilik plana 5 davet gönderip hepsi kabul edilince
   * 10 üye olur.
   */
  seats: { used: number; limit: number; remaining: number };
}

export const inviteSchema = z.object({
  email: z.email('Geçerli bir e-posta adresi girin').max(254).transform((v) => v.trim().toLowerCase()),
  role: workspaceRoleSchema,
});
export type InviteInput = z.infer<typeof inviteSchema>;

/**
 * Davet yanıtı `emailSent` taşır ve SMTP kapalıyken `false` döner.
 *
 * Bu bilinçli: mail gitmediği hâlde "davet gönderildi" demek desteğe yalan
 * söyler. `false` gelince arayüz davet bağlantısını KOPYALANABİLİR gösterir —
 * kullanıcı onu WhatsApp'tan yollar ve iş yürür.
 *
 * `inviteUrl` yalnızca `emailSent: false` iken ve YALNIZCA daveti gönderene
 * döner. Ham jeton başka hiçbir yanıtta görünmez; DB'de `tokenHash` var.
 */
export interface InviteResponse {
  invitation: InvitationRow;
  emailSent: boolean;
  inviteUrl: string | null;
}

export const INVITATION_TTL_DAYS = 7;

/// Daveti kabul — @Public, jetonla. Kullanıcının hesabı OLMAYABİLİR.
export const invitationAcceptSchema = z.object({
  token: z.string().min(20, 'Bağlantı geçersiz'),
});
export type InvitationAcceptInput = z.infer<typeof invitationAcceptSchema>;

/// Kimliksiz önizleme: "X sizi Y'ye davet etti" ekranı için. Jetonun
/// geçerliliğini sızdırmadan hangi workspace olduğunu göstermek gerekiyor.
export interface InvitationPreview {
  workspaceName: string;
  invitedByName: string | null;
  role: WorkspaceRole;
  email: string;
  expiresAt: string;
  /// Bu e-postayla kayıtlı bir hesap var mı — arayüz "giriş yap" mı
  /// "kayıt ol" mu göstereceğine buna bakar.
  accountExists: boolean;
  valid: boolean;
}

export const roleChangeSchema = z.object({
  role: workspaceRoleSchema,
});
export type RoleChangeInput = z.infer<typeof roleChangeSchema>;

/// EK7 — sahiplik devri. Çift onay: hedef üyenin e-postası birebir yazılır.
export const ownershipTransferSchema = z.object({
  targetUserId: z.uuid(),
  confirmEmail: z.email(),
});
export type OwnershipTransferInput = z.infer<typeof ownershipTransferSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Profil, oturumlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * GV1 — profil ekranının okuduğu şekil (`GET /me/profile`).
 *
 * 🔴 Bu arayüz bir tur `profile.service.ts` içinde yaşadı. Sözleşme orada
 * kaldığı sürece ARAYÜZ ONU OKUYAMIYORDU: web `@mentorivo/api`'yi import
 * etmiyor, edemez de. Ayarlar ekranı yazılırken tek seçenek şekli ikinci kez
 * elle yazmaktı — ve iki kopyadan biri er geç geride kalır, üstelik sessizce:
 * eksik bir alan `undefined` olur, ekran boş basar, kimse hata görmez.
 */
export interface MeProfileResponse {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  locale: Locale;
  emailVerified: boolean;
  /// Sosyal girişle açılmış hesapta `passwordHash` null olabiliyor; parola
  /// değiştirme formu "mevcut parola" istemek yerine "parola belirle" der.
  hasPassword: boolean;
  /// Bekleyen e-posta değişikliği — arayüz "yeni adrese doğrulama gönderildi"
  /// şeridini gösteriyor. Sunucuda Redis'te tutuluyor.
  pendingEmail: string | null;
}

export const profileUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    locale: localeSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.locale !== undefined, 'Değiştirilecek bir şey verin');
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;

/**
 * E-posta değişimi İKİ AŞAMALI: yeni adrese doğrulama gider, eski adres
 * doğrulanana kadar GEÇERLİ KALIR. Tek aşamalı olsaydı yazım hatası yapan
 * kullanıcı hesabından kalıcı olarak kilitlenirdi.
 */
export const emailChangeSchema = z.object({
  newEmail: z.email().max(254).transform((v) => v.trim().toLowerCase()),
  /// Mevcut parola — oturum çalınmışsa e-posta ele geçirilmesin.
  currentPassword: z.string().min(1),
});
export type EmailChangeInput = z.infer<typeof emailChangeSchema>;

/**
 * Parola değişiminde DİĞER tüm oturumlar iptal edilir, mevcut oturum kalır.
 * Parola değiştirmenin en yaygın sebebi "birisi hesabıma girdi" şüphesi;
 * diğer oturumları açık bırakmak o kullanıcıyı korumamak olurdu.
 */
export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1, 'Mevcut parolanızı girin'),
  newPassword: z.string().min(8, 'Parola en az 8 karakter olmalı').max(200),
});
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;

/// GV2 — `AdminSessionRow` ile AYNI şekil, bilerek: aynı UI bileşeni iki
/// panelde de kullanılabilsin.
export interface SessionRow {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  active: boolean;
  /// İsteği yapan oturumun kendisi. Bu ekrandan kapatılamaz — çıkış için
  /// `/auth/logout` var; buradan kapatmak "neden çıktım" sorusunu doğurur.
  current: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// GDPR / KVKK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * GV5 — veri dışa aktarma. Kuyruk işi; sonuç S3'e yazılır ve 7 gün geçerli
 * imzalı bağlantı bildirimle gelir.
 *
 * İçerik: üyeler, bağlantılar (JETONSUZ), hesaplar, kampanya hiyerarşisi,
 * metrikler, kurallar ve çalıştırma kayıtları, kreatif META VERİSİ (dosyalar
 * değil — arşiv gigabaytlara çıkmasın), kredi defteri, denetim kaydı.
 *
 * Günde bir kez; ağır bir iş ve tekrar tetiklemek kuyruğu tıkar.
 */
export const DATA_EXPORT_MAX_PER_DAY = 1;
export const DATA_EXPORT_URL_TTL_DAYS = 7;

export interface DataExportJobResponse {
  jobId: string;
  status: 'queued' | 'running' | 'ready' | 'failed';
  requestedAt: string;
  downloadUrl: string | null;
  expiresAt: string | null;
}

/**
 * GV6 — hesap silme. Workspace silmeden AYRI bir iştir.
 *
 *  (a) Tüm oturumlar iptal edilir
 *  (b) Kullanıcının TEK üyesi olduğu workspace'ler tümüyle silinir
 *      (jetonlar + S3 dahil)
 *  (c) Başka üyesi olan ve kullanıcının OWNER olduğu workspace'ler için
 *      ÖNCE DEVİR ZORUNLU — istek `OWNERSHIP_TRANSFER_REQUIRED` ile
 *      reddedilir ve hangi workspace'ler olduğu `details`'te döner.
 *
 * Aksi hâlde bir kişinin hesabını silmesi, ekibin verisini de siler.
 */
export const accountDeleteSchema = z.object({
  currentPassword: z.string().min(1),
  /// Kullanıcının kendi e-postasını birebir yazması.
  confirmEmail: z.email(),
});
export type AccountDeleteInput = z.infer<typeof accountDeleteSchema>;

export interface AccountDeleteImpact {
  /// Tümüyle silinecek workspace'ler (tek üyesi bu kullanıcı).
  workspacesToDelete: { id: string; name: string; memberCount: number }[];
  /// Önce devir gereken workspace'ler.
  workspacesNeedingTransfer: { id: string; name: string; memberCount: number }[];
  /// Sadece üyelikten çıkılacaklar.
  workspacesToLeave: { id: string; name: string }[];
  connectionCount: number;
  creativeCount: number;
  canProceed: boolean;
}

/// GV7 — bağlı platform izinleri. İptal yolu integrations'a bağlanır.
export interface ConnectedPlatformPermission {
  connectionId: string;
  platform: string;
  displayName: string | null;
  scopes: string[];
  grantedAt: string;
  status: ConnectionStatus;
}

/**
 * 2FA (TOTP) §14 gereği v1'DE YOK ve bu turda YAZILMAYACAK.
 *
 * Açıkça kayda geçiyor ki sonraki ajan burada bir boşluk sanıp uç yazmasın.
 * Arayüzde yeri ayrılır ve "yakında" olarak KAPALI görünür.
 */
export const TWO_FACTOR_AVAILABLE = false;

// ═══════════════════════════════════════════════════════════════════════════
// Admin ek uçları (mevcut /admin/workspaces ile ÇAKIŞMAZ)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bugün admin sahipsiz workspace'i GÖRÜYOR ama DÜZELTEMİYOR — DB'de gerçekten
 * bir tane var (0 üye, 52 denetim kaydı, 25 kredi). "Buradan yapılamıyor"
 * ile biten tek somut destek senaryosu bu; iki uç onu kapatıyor.
 */
export const adminAssignOwnerSchema = z.object({
  userId: z.uuid(),
  reason: z.string().trim().min(3).max(500),
});
export type AdminAssignOwnerInput = z.infer<typeof adminAssignOwnerSchema>;

export const adminWorkspaceDeleteSchema = z.object({
  confirmSlug: z.string().min(1).max(80),
  reason: z.string().trim().min(3).max(500),
});
export type AdminWorkspaceDeleteInput = z.infer<typeof adminWorkspaceDeleteSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

export const WorkspaceErrorCode = {
  WORKSPACE_NOT_FOUND: 'WORKSPACE_NOT_FOUND',
  /// Kullanıcı başına workspace sınırı doldu.
  WORKSPACE_LIMIT: 'WORKSPACE_LIMIT',
  /// Silme onayında yazılan ad/slug eşleşmedi.
  CONFIRMATION_MISMATCH: 'CONFIRMATION_MISMATCH',
  /// Plan üye limiti (bekleyen davetler dahil). `details: {limit, used}`.
  MEMBER_LIMIT: 'MEMBER_LIMIT',
  /// Bu e-posta zaten üye.
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  /// Bekleyen davet var — yenisi açılmaz, mevcut tazelenir.
  INVITATION_PENDING: 'INVITATION_PENDING',
  INVITATION_NOT_FOUND: 'INVITATION_NOT_FOUND',
  INVITATION_EXPIRED: 'INVITATION_EXPIRED',
  /// Davet başka bir e-postaya ait; giriş yapan kullanıcı eşleşmiyor.
  INVITATION_EMAIL_MISMATCH: 'INVITATION_EMAIL_MISMATCH',
  /// Son OWNER düşürülemez / çıkarılamaz / ayrılamaz.
  LAST_OWNER: 'LAST_OWNER',
  /// OWNER rolünü yalnız OWNER verebilir.
  OWNER_ROLE_RESTRICTED: 'OWNER_ROLE_RESTRICTED',
  /// Kullanıcı kendi rolünü değiştiremez.
  SELF_ROLE_CHANGE_FORBIDDEN: 'SELF_ROLE_CHANGE_FORBIDDEN',
  /// Devir hedefi bu workspace'in üyesi değil.
  TRANSFER_TARGET_NOT_MEMBER: 'TRANSFER_TARGET_NOT_MEMBER',
  /// Hesap silinmeden önce sahiplik devri gerekiyor.
  OWNERSHIP_TRANSFER_REQUIRED: 'OWNERSHIP_TRANSFER_REQUIRED',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  EMAIL_TAKEN: 'EMAIL_TAKEN',
  INVALID_TIMEZONE: 'INVALID_TIMEZONE',
  INVALID_CURRENCY: 'INVALID_CURRENCY',
  /// Günlük dışa aktarma hakkı doldu.
  EXPORT_TOO_SOON: 'EXPORT_TOO_SOON',
  /// Kendi oturumunu bu ekrandan kapatamaz.
  SELF_SESSION_REVOKE_FORBIDDEN: 'SELF_SESSION_REVOKE_FORBIDDEN',
} as const;
export type WorkspaceErrorCode = (typeof WorkspaceErrorCode)[keyof typeof WorkspaceErrorCode];

export const workspaceMemberListQuerySchema = listQuerySchema.extend({
  role: workspaceRoleSchema.optional(),
  sort: z.enum(['joinedAt', 'email', 'role']).default('joinedAt'),
  dir: sortDirSchema,
});
